import { BreakpointObserver } from "@angular/cdk/layout";
import { Component, inject, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { RouterModule } from "@angular/router";
import { UiKitModule } from "@app/ui-kit.module";
import { ProjectCardComponent } from "@components/project-cards/project-card/project-card.component";
import { ProjectImportComponent } from "@components/project-import/project-import.component";
import { CloudProjectMetadata } from "@domain/project/cloud-project-metadata";
import { LocalizeProjectTypePipe } from "@domain/project/localize-project-type.pipe";
import type { ScopedProject } from "@domain/project/project-guards";
import { ProjectStatusService } from "@domain/project/project-status.service";
import { ProjectType } from "@domain/project/project-type.enum";
import { ProjectService } from "@domain/project/project.service";
import { ProjectStatusDto } from "@domain/project/template/property/project/details/information/project-status.dto";
import { TemplateService } from "@domain/project/template/template.service";
import { TranslateProjectStatusPipe } from "@domain/project/translate-project-status.pipe";
import { UserService } from "@domain/user/user.service";
import { environment } from "@environments/environment";
import { BusinessScope } from "@lib/constants/business-scope";
import { DropdownDirective } from "@odx/angular/components/dropdown";
import { MainfilterGroupComponent } from "@odx/angular/components/mainfilter-group";
import { plainToInstance } from "class-transformer";
import { combineLatestWith, debounceTime, filter, forkJoin, map, Observable, of, shareReplay, startWith, switchMap } from "rxjs";
import { Breakpoints } from "../../styles/breakpoints";

@Component({
  selector: "app-home",
  templateUrl: "./home.component.html",
  styleUrls: ["./home.component.scss"],
  imports: [
    UiKitModule,
    RouterModule,
    ProjectCardComponent,
    ProjectImportComponent,
    MainfilterGroupComponent,
    ReactiveFormsModule,
    LocalizeProjectTypePipe,
    TranslateProjectStatusPipe,
  ],
  providers: [ProjectImportComponent],
})
export class HomeComponent implements OnInit {
  private projectService = inject(ProjectService);
  private userService = inject(UserService);
  private projectStatusService = inject(ProjectStatusService);
  private templateService = inject(TemplateService);
  private formBuilder = inject(FormBuilder);
  private breakpointObserver = inject(BreakpointObserver);

  projects$!: Observable<ScopedProject[]>;
  metadata$!: Observable<CloudProjectMetadata[]>;
  isMobile$ = this.breakpointObserver.observe(Breakpoints.mobile).pipe(map((result) => result.matches));
  filteredProjects$!: Observable<ScopedProject[]>;

  @ViewChild("filterDropdown") filterDropdown!: DropdownDirective;

  crmUrl = environment.crmUrl;

  filterFormGroup!: FormGroup;
  mobileFilterFormGroup!: FormGroup;
  protected projectTypes: ProjectType[] = Object.values(ProjectType);
  protected projectStatuses: ProjectStatusDto[] = [];

  protected userId$: Observable<string>;
  protected scope$: Observable<BusinessScope | undefined>;
  protected isNewProjectVisible$: Observable<boolean>;

  constructor() {
    const user$ = this.userService.user$.pipe(shareReplay(1));

    this.scope$ = user$.pipe(map((user) => user.scope));
    this.userId$ = user$.pipe(map((user) => user.id));
    this.isNewProjectVisible$ = user$.pipe(
      switchMap((user) => this.templateService.generateTemplate(user.scope!, user.country)),
      map((template) => {
        return template?.properties?.general?.homeMenu?.newProject?.visible ?? true;
      }),
    );

    this.projects$ = this.loadProjects();
  }

  ngOnInit(): void {
    this.filterFormGroup = this.formBuilder.group({
      type: [[]],
      status: [[]],
      search: [""],
    });
    this.mobileFilterFormGroup = this.formBuilder.group({
      type: [[]],
      status: [[]],
      search: [""],
    });
    this.filterProjects();
    this.scope$
      .pipe(
        filter((scope) => !!scope),
        switchMap((scope) => this.projectStatusService.getProjectStatuses(scope)),
        map((statuses) => statuses ?? []),
      )
      .subscribe((statuses) => {
        this.projectStatuses = statuses;
      });
  }

  protected clearFilters() {
    this.filterFormGroup.reset();
    this.mobileFilterFormGroup.reset();
  }

  protected applyMobileFilters() {
    this.filterFormGroup.setValue(this.mobileFilterFormGroup.value);
    this.filterDropdown.close();
  }

  protected dismissMobileFilters() {
    this.clearFilters();
    this.filterDropdown.close();
  }

  protected goToCrm() {
    window.location.href = this.crmUrl;
  }

  private filterProjects() {
    const filters$ = this.filterFormGroup.valueChanges.pipe(debounceTime(500), startWith(this.filterFormGroup.value));

    this.filteredProjects$ = this.projects$.pipe(
      combineLatestWith(filters$, this.metadata$, this.userId$),
      map(([projects, filters, metadata, userId]) => {
        if (!this.hasActiveFilters(filters)) {
          return projects;
        }

        const { search, type, status } = filters;

        return projects.filter((project) => {
          const matchesSearch =
            !search ||
            project.name.toLowerCase().includes(search.toLowerCase()) ||
            project.placeName?.toLowerCase().includes(search.toLowerCase()) ||
            project.customer.name?.toLowerCase().includes(search.toLowerCase());

          const matchesStatus = !status?.length || status.includes(project.status);

          const matchesType = this.filterProjectByType(type, userId, metadata, project);

          return matchesSearch && matchesStatus && matchesType;
        });
      }),
    );
  }

  private filterProjectByType(type: string[], userId: string, metadata: CloudProjectMetadata[], project: ScopedProject) {
    const projectMetadata: CloudProjectMetadata = plainToInstance(
      CloudProjectMetadata,
      metadata.find((metadata: CloudProjectMetadata) => project.cloudId === metadata.id),
    );

    if (projectMetadata) {
      const rawUser = projectMetadata.users[0] as any;

      const isCollaboration = projectMetadata.users.length > 1;
      const isOwned = rawUser.id === userId && rawUser.role === "OWNER";
      const isNotOwned = rawUser.id !== userId && rawUser.role === "OWNER";

      return (
        !type?.length ||
        ((!type.includes(ProjectType.COLLABORATION) || isCollaboration) &&
          (!type.includes(ProjectType.OWNED) || isOwned) &&
          (!type.includes(ProjectType.NOT_OWNED) || isNotOwned))
      );
    }
    return false;
  }

  private loadProjects(): Observable<ScopedProject[]> {
    const loadedProjects$ = this.projectService.projects$.pipe(
      map((projects) => projects.sort((a, b) => b.lastModified!.getTime() - a.lastModified!.getTime())),
      shareReplay(1),
    );

    this.metadata$ = loadedProjects$.pipe(
      switchMap((projects) => {
        const metadata = projects
          .filter((project) => project.cloudId)
          .map((project) => {
            return this.projectService.getLocalMetadataById(project.cloudId!);
          });
        return metadata.length === 0 ? of([]) : forkJoin(metadata);
      }),
    );
    return loadedProjects$;
  }

  private hasActiveFilters(filters: any) {
    return (
      (filters.search && filters.search.trim().length > 0) ||
      (filters.type && filters.type.length > 0) ||
      (filters.status && filters.status.length > 0)
    );
  }
}
